import { useState, useEffect } from 'react';

import { ModalTitle } from './modal-elements/ModalTitle';
import { ModalToggle } from './modal-elements/ModalToggle';
import { ModalLink } from './modal-elements/ModalLink';
import { ModalAflIdPromo } from './modal-elements/ModalAflIdPromo';
import { ModalNote } from './modal-elements/ModalNote';
import { ModalButton } from './modal-elements/ModalButton';
import { ModalAccount } from './modal-elements/ModalAccount';

import { useSiteSettings } from 'common/react/hooks/use-site-settings';
import { getTranslation } from 'common/react/utils/translations';

export const useModalContentFactory = () => {
    const [componentsWithProps, setComponentsWithProps] = useState(null);
    const { siteSettings, setSiteSettings, settingsConfig } = useSiteSettings();

    const selectedRegion = siteSettings[settingsConfig.region.key];
    const hideBettingSetting = siteSettings[settingsConfig.hideBetting.key];
    const isOVer18Setting = siteSettings[settingsConfig.over18.key];

    const modalContainer = document.querySelector('.js-site-settings-overlay');
    const regionsPage = modalContainer.dataset.regionsPage;

    const pageHasAflIdLoginButton = document.querySelector('.js-aflid-user');

    useEffect(() => {
        if (!modalContainer) {
            return;
        }

        const fetchModalContent = async () => {
            try {
                const isAuthenticated =
                    await PULSE.app.authClient.isAuthenticated();
                let components = [];
                if (!isAuthenticated) {
                    components = [
                        {
                            component: ModalTitle,
                            props: {
                                icon: 'settings',
                                title: getTranslation('label.settings')
                            }
                        },
                        {
                            component: ModalAflIdPromo,
                            props: {
                                text: getTranslation(
                                    'settingsModal.aflId.text'
                                ),
                                buttons: [
                                    {
                                        label: getTranslation(
                                            'label.aflid.create'
                                        ),
                                        onClick: () =>
                                            PULSE.app.authClient.signInWithRedirect()
                                    },
                                    {
                                        label: getTranslation(
                                            'label.aflid.login'
                                        ),
                                        onClick: () =>
                                            PULSE.app.authClient.signInWithRedirect()
                                    }
                                ]
                            }
                        },
                        {
                            component: ModalToggle,
                            props: {
                                icon: 'sportsbet',
                                label: getTranslation('label.hideBettingOdds'),
                                isChecked: hideBettingSetting,
                                onChange: (checked) =>
                                    setSiteSettings({
                                        [settingsConfig.hideBetting.key]:
                                            checked
                                    }),
                                notes: getTranslation(
                                    'settingsModal.bettingOdds.note'
                                )
                            }
                        },
                        {
                            component: ModalLink,
                            props: {
                                icon: 'globe',
                                label: getTranslation('label.filters.region'),
                                link: `https://${
                                    PULSE.app.paths.domain + regionsPage
                                }`,
                                btnLabel: selectedRegion.name
                            }
                        }
                    ];
                } else if (isAuthenticated) {
                    components = [
                        {
                            component: ModalTitle,
                            props: {
                                icon: 'settings',
                                title: getTranslation('label.settings'),
                                modalToClose: '.js-site-settings-modal',
                                modalElement: '.js-site-settings-overlay__modal'
                            }
                        },
                        {
                            component: ModalAccount
                        },
                        {
                            component: ModalNote,
                            props: {
                                text: getTranslation(
                                    'settingsModal.accountSettings.note'
                                )
                            }
                        },
                        {
                            component: ModalToggle,
                            props: {
                                icon: 'id-card',
                                label: getTranslation('label.18OrOver'),
                                isChecked:
                                    siteSettings[settingsConfig.over18.key],
                                onChange: (checked) => {
                                    setSiteSettings({
                                        [settingsConfig.over18.key]: checked
                                    });
                                    if (!checked) {
                                        setSiteSettings({
                                            [settingsConfig.hideBetting
                                                .key]: true
                                        });
                                    }
                                },
                                cssClass: 'first-of-stack'
                            }
                        },
                        {
                            component: ModalToggle,
                            props: {
                                icon: 'sportsbet',
                                label: getTranslation('label.hideBettingOdds'),
                                isChecked:
                                    siteSettings[
                                        settingsConfig.hideBetting.key
                                    ],
                                isDisabled: !isOVer18Setting,
                                onChange: (checked) =>
                                    setSiteSettings({
                                        [settingsConfig.hideBetting.key]:
                                            checked
                                    }),
                                notes: getTranslation(
                                    'settingsModal.bettingOdds.note'
                                ),
                                cssClass: 'last-of-stack'
                            }
                        },
                        {
                            component: ModalLink,
                            props: {
                                icon: 'globe',
                                label: getTranslation('label.filters.region'),
                                link: `https://${PULSE.app.paths.domain}/select-region`,
                                btnLabel: selectedRegion.name
                            }
                        },
                        {
                            component: ModalButton,
                            props: {
                                icon: 'paper-plane',
                                label: getTranslation('label.comPreferences'),
                                description: getTranslation(
                                    'settingsModal.comPreferences.description'
                                ),
                                onClick: () =>
                                    PULSE.app.common.siteSettings.showPreferences(),
                                btnLabel: getTranslation('label.edit')
                            }
                        }
                    ];
                }

                /**
                 * Only render the promo if the user is logged out and the page
                 * has an afl id login button configured in the nav
                 */
                if (!isAuthenticated && !pageHasAflIdLoginButton) {
                    components = components.filter(
                        (item) => item.component !== ModalAflIdPromo
                    );
                }

                setComponentsWithProps(components);
            } catch (error) {
                console.error('Error fetching modal content:', error);
            }
        };

        fetchModalContent();
    }, [isOVer18Setting, hideBettingSetting, selectedRegion]);

    return { componentsWithProps };
};
